<template>
  <div id="app">
    <transition name="page" mode="out-in">
      <router-view />
    </transition>
  </div>
</template>

<script>
const APP_DATA_VERSION = '3.1-real-backend-260509'

export default {
  name: 'App',
  async created() {
    // 版本检查：大版本切换时强制清理旧 localStorage，防止 mock/真实数据混杂
    const savedVersion = localStorage.getItem('tr_app_data_version')
    if (savedVersion !== APP_DATA_VERSION) {
      this.clearAllLegacyData()
      localStorage.setItem('tr_app_data_version', APP_DATA_VERSION)
      if (this.$router && this.$route && this.$route.path !== '/login') {
        this.$router.replace('/login')
      }
      return
    }

    // 若已登录，先验证 token 是否仍被后端认可
    if (this.$store.state.user && this.$store.state.user.token) {
      const isValid = await this.validateAuth()
      if (!isValid) {
        this.$store.commit('LOGOUT')
        this.$router.replace('/login')
        return
      }
      // 认证通过后才从后端加载数据
      await this.initBackendData()
    }

    // 将数据初始化方法绑定到 $root，供登录成功后调用
    this.$root.initBackendData = this.initBackendData
  },
  methods: {
    clearAllLegacyData() {
      // 批量清理所有可能与旧 mock 数据相关的 localStorage key
      const keysToRemove = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (!key) continue
        if (
          key.startsWith('talent_retention_v3_') ||
          key.startsWith('tr_v3_') ||
          key === 'user' ||
          key === 'evaluations' ||
          key === 'currentEvaluation' ||
          key === 'role'
        ) {
          keysToRemove.push(key)
        }
      }
      keysToRemove.forEach(k => localStorage.removeItem(k))
    },
    async validateAuth() {
      try {
        await this.$api.fetchEmployees()
        return true
      } catch (e) {
        // 任何认证失败或网络不通都视为无效，强制重新登录
        // 不允许使用本地缓存数据，防止假数据误导
        return false
      }
    },
    async initBackendData() {
      // 应用启动时清空所有业务数据，防止本地旧/mock数据干扰
      // 数据必须从后端 API 获取，失败时页面显示空状态而非假数据
      this.clearBusinessData()

      try {
        const tasksRes = await this.$api.fetchTasks()
        if (tasksRes.data) {
          this.$store.commit('SET_TASKS_LIST', tasksRes.data)
          // 根据当前路由自动恢复 currentTask，防止任务层页面刷新后菜单消失
          const path = this.$route && this.$route.path || ''
          const match = path.match(/\/admin\/tasks\/(\d+)/)
          if (match) {
            const taskId = parseInt(match[1], 10)
            await this.$store.dispatch('initCurrentTask', { taskId })
          }
        }
      } catch (e) {
        console.warn('[App] 获取任务列表失败:', e.message)
      }

      try {
        const empRes = await this.$api.fetchEmployees()
        if (empRes.data) {
          this.$store.commit('SET_EMPLOYEES', empRes.data)
        }
      } catch (e) {
        console.warn('[App] 获取员工列表失败:', e.message)
      }

      try {
        const behRes = await this.$api.fetchBehaviors()
        if (behRes.data) {
          this.$store.commit('SET_BEHAVIORS', behRes.data)
        }
      } catch (e) {
        console.warn('[App] 获取行为库失败:', e.message)
      }

      try {
        const catRes = await this.$api.fetchBehaviorCategories()
        if (catRes.data) {
          this.$store.commit('SET_CATEGORIES', catRes.data)
        }
      } catch (e) {
        console.warn('[App] 获取行为分类失败:', e.message)
      }

      try {
        const settingsRes = await this.$api.fetchSettings()
        if (settingsRes.data) {
          // 后端返回的是 {config_key: config_value} 的平铺结构，需要归一化为前端期望的嵌套结构
          const raw = settingsRes.data
          const normalized = raw.settings || {
            thresholds: raw.thresholds || { lowMax: 4, mediumMax: 10 },
            labels: raw.labels || { low: '低风险', medium: '中风险', high: '高风险' },
            survey: raw.survey || { expireDays: 3, baseUrl: 'http://localhost:8080/#/survey/' },
            system: raw.system || { name: 'AI骨干人才保留助手', version: 'v3.1' }
          }
          this.$store.commit('SET_SETTINGS', normalized)
        }
      } catch (e) {
        console.warn('[App] 获取系统配置失败:', e.message)
      }

      try {
        const instrRes = await this.$api.fetchInstructions()
        if (instrRes.data) {
          this.$store.commit('SET_INSTRUCTIONS', instrRes.data)
        }
      } catch (e) {
        console.warn('[App] 获取评估说明失败:', e.message)
      }

      try {
        const rulesRes = await this.$api.fetchScoringRules()
        if (rulesRes.data) {
          this.$store.commit('SET_SCORING_RULES', rulesRes.data)
        }
      } catch (e) {
        console.warn('[App] 获取打分规则失败:', e.message)
      }

      try {
        const surveyRes = await this.$api.fetchSurveys()
        if (surveyRes.data) {
          this.$store.commit('SET_SURVEYS', surveyRes.data)
        }
      } catch (e) {
        console.warn('[App] 获取调研列表失败:', e.message)
      }

      try {
        const evalRes = await this.$api.fetchAdminEvaluations()
        if (evalRes.data) {
          this.$store.commit('SET_EVALUATIONS', evalRes.data)
        }
      } catch (e) {
        console.warn('[App] 获取评估记录失败:', e.message)
      }

      try {
        const resignedRes = await this.$api.fetchResigned()
        if (resignedRes.data) {
          this.$store.commit('SET_RESIGNED', resignedRes.data)
        }
      } catch (e) {
        console.warn('[App] 获取离职员工失败:', e.message)
      }

      try {
        const feedbackRes = await this.$api.fetchDepartureFeedbacks()
        if (feedbackRes.data) {
          this.$store.commit('SET_FEEDBACKS', feedbackRes.data)
        }
      } catch (e) {
        console.warn('[App] 获取离职反馈失败:', e.message)
      }

      try {
        const adminRes = await this.$api.fetchAdmins()
        if (adminRes.data) {
          this.$store.commit('SET_ADMINS', adminRes.data)
        }
      } catch (e) {
        console.warn('[App] 获取管理员列表失败:', e.message)
      }

      try {
        const contentRes = await this.$api.fetchContents()
        if (contentRes.data) {
          this.$store.commit('SET_CONTENTS', contentRes.data)
        }
      } catch (e) {
        console.warn('[App] 获取内容列表失败:', e.message)
      }
    },
    clearBusinessData() {
      // 清空所有业务数据，确保不从本地缓存读取旧/mock数据
      this.$store.commit('SET_EMPLOYEES', [])
      this.$store.commit('SET_RESIGNED', [])
      this.$store.commit('SET_BEHAVIORS', [])
      this.$store.commit('SET_CATEGORIES', [])
      this.$store.commit('SET_SETTINGS', {})
      this.$store.commit('SET_INSTRUCTIONS', [])
      this.$store.commit('SET_SCORING_RULES', { content: '' })
      this.$store.commit('SET_TASKS_LIST', [])
      this.$store.commit('SET_CASES', [])
      this.$store.commit('SET_SURVEYS', [])
      this.$store.commit('SET_FEEDBACKS', [])
      this.$store.commit('SET_ADMINS', [])
      this.$store.commit('SET_CONTENTS', [])
      this.$store.commit('SET_USERS', [])
      this.$store.commit('SET_EVALUATIONS', [])
      this.$store.commit('SET_CURRENT_EVALUATION', null)
    }
  }
}
</script>

<style>
#app {
  background: var(--color-bg);
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans SC", sans-serif;
}
</style>
